import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ClockIcon } from '../../assets';
import { theme } from '../../theme';
import {
  getTimeLeftLong,
  getTimeLeftMinimal,
  getTimeLeftShort,
  hexToRgba,
  TimeLeftType,
} from '../../utils';
import { UIText, UITextSize } from '../typography';

type Format = 'short' | 'long' | 'minimal';

export type TimerType = {
  closingTime?: number;
  timerText?: string;
  endedText?: string;
  format?: Format;
  fill?: string;
  color?: string;
  scale?: 'xs' | 'sm';
  size?: number;
  hasClockIcon?: boolean;
  highlight?: boolean;
};

const TimerBase = styled.div<{ scale?: 'xs' | 'sm' }>`
  display: flex;
  gap: ${() => theme.spacings[1].rem};
  align-items: center;
  white-space: nowrap;
  color: ${(props) => props.color || theme.colors.neutrals.solidGrays[3]};
`;
const HighlightContainer = styled.div<{ color?: string }>`
  width: fit-content;
  padding: 4px;

  border-radius: 4px;
  ${({ theme, color }) => `
      background-color: ${hexToRgba(
        color || theme.colors.system.systemGreen,
        0.08
      )};
      color: ${color || theme.colors.system.systemGreen};
  `}
`;

const IconText = styled(UIText)`
  line-height: 20px;
`;

export const Timer: React.FC<TimerType> = ({
  closingTime,
  format,
  timerText,
  endedText,
  fill,
  color,
  size,
  scale,
  hasClockIcon = true,
  highlight,
}) => {
  const [countdown, setCountdown] = useState<TimeLeftType>({
    timeLeft: '',
    interval: 1000,
  });

  useEffect(() => {
    const updateCountdown = (e: any) =>
      closingTime && setCountdown(e(closingTime));

    if (format === 'short') {
      updateCountdown(getTimeLeftShort);
    } else if (format === 'minimal') {
      updateCountdown(getTimeLeftMinimal);
    } else {
      updateCountdown(getTimeLeftLong);
    }

    const interval = setInterval(() => {
      updateCountdown(
        format === 'short'
          ? getTimeLeftShort
          : format === 'minimal'
          ? getTimeLeftMinimal
          : getTimeLeftLong
      );
    }, countdown?.interval);

    return () => clearInterval(interval);
  }, [closingTime, countdown?.interval, format]);

  if (!countdown.timeLeft && !endedText) {
    return null;
  }

  const contentText = countdown.timeLeft ? timerText : endedText;
  const Container = highlight ? HighlightContainer : Fragment;

  const scaleProps = {
    clock: {
      width: size || 12,
      fill: fill || theme.colors.neutrals.solidGrays[3],
    },
    text: {
      size: 'xs' as UITextSize,
      color: color || theme.colors.neutrals.solidGrays[3],
    },
  };
  if (scale === 'sm') {
    scaleProps.clock.width = 16;
    scaleProps.text.size = 'sm';
  }

  return (
    <Container {...(highlight ? { color: fill } : {})}>
      <TimerBase scale={scale}>
        {hasClockIcon && (
          <ClockIcon
            width={scaleProps.clock.width}
            fill={scaleProps.clock.fill}
          />
        )}
        <IconText size={scaleProps.text.size} color={scaleProps.text.color}>
          {contentText}
          {countdown.timeLeft}
        </IconText>
      </TimerBase>
    </Container>
  );
};
